import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentClientIc, EmptyContentIc } from '../../../../components/icons';
import { OrdersTableProps, PartnerOrderData } from '../types';
import { getTableColumns } from '../constants';
import { OrderIdCell, ModelCell, OfferCell, StatusCell, DateCell } from './index';

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onShowContent,
  onOrderAction,
  actionOrderId,
}) => {
  const { t } = useTranslations();

  const renderCell = useCallback((order: PartnerOrderData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <OrderIdCell order={order} />;
      case "model":
        return <ModelCell order={order} onShowContent={onShowContent} />;
      case "offer":
        return <OfferCell order={order} />;
      case 'status':
        return (
          <StatusCell
            order={order}
            onOrderAction={onOrderAction}
            actionOrderId={actionOrderId}
          />
        );
      case 'createdAt':
        return <DateCell order={order} />;
      default:
        return null;
    }
  }, [onShowContent, onOrderAction, actionOrderId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      data={orders}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.orders.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.orders.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default OrdersTable;
